import React, { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { clearCart, openPayment } from "../../redux/cartSlice";
import { useSelector, useDispatch } from "react-redux";
import { IoMdClose } from "react-icons/io";
import axios from "axios";
import "./payment.css";

// This is publishable API key.
const KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// const stripePromise = loadStripe(KEY);

export default function PaymentForm() {
  const cart = useSelector((state) => state.cart);
  // eslint-disable-next-line no-unused-vars
  const [stripePromise, setStripePromise] = useState(() => loadStripe(KEY));

  const [clientSecret, setClientSecret] = useState("");

  useEffect(() => {
    // Create PaymentIntent as soon as the page loads
    fetch(process.env.REACT_APP_API_URL + "/checkout/payment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(cart),
    })
      .then((res) => res.json())
      .then((data) => setClientSecret(data.clientSecret));
  }, [cart]);

  const appearance = {
    theme: "stripe",
  };
  const options = {
    clientSecret,
    appearance,
  };

  return (
    <div className="PaymentForm absolute top-0 z-40 bg-black bg-opacity-70">
      {clientSecret && (
        <Elements options={options} stripe={stripePromise} key={clientSecret}>
          <CheckoutForm />
        </Elements>
      )}
    </div>
  );
}

function CheckoutForm() {
  const cart = useSelector((state) => state.cart);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    country: "",
    address: "",
  });
  useEffect(() => {
    if (!stripe) {
      return;
    }

    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret) {
      return;
    }

    stripe.retrievePaymentIntent(clientSecret).then(({ paymentIntent }) => {
      switch (paymentIntent.status) {
        case "succeeded":
          setMessage("Payment succeeded!");
          break;
        case "processing":
          setMessage("Your payment is processing.");
          break;
        case "requires_payment_method":
          setMessage("Your payment was not successful, please try again.");
          break;
        default:
          setMessage("Something went wrong.");
          break;
      }
    });
  }, [dispatch, stripe]);

  const handleInputData = (e) => {
    const { value, name } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const submitFormData = (e) => {
    e.preventDefault();
    if (
      formData.fullName.length === 0 ||
      formData.email.length === 0 ||
      formData.country.length === 0 ||
      formData.address.length === 0
    ) {
      setError(true);
    } else {
      setStep(step + 1);
    }
  };

  const saveOrderToDB = async (data) => {
      const { amount = cart.total } = data;
      let products = []
    for(let i in cart.products) {
      i = {
        productId: cart.products[i]._id,
        quantity: cart.products[i].quantity,
        productName: cart.products[i].title,
        price: cart.products[i].price,
        total: cart.products[i].price * cart.products[i].quantity,
      }
      products.push(i)
    }
    const order = {
      name: formData.fullName, email: formData.email, quantity: cart.quantity, address: formData.address, amount: amount, country: formData.country
    }
    await axios.post("http://localhost:8000/api/order", order)
    .then(dispatch(clearCart()))
    .catch(err => console.log(err.message))
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return;
    }

    setIsLoading(true);

    await stripe
      .confirmPayment({
        elements,
        confirmParams: {
          // Make sure to change this to your payment completion page
          return_url: "http://localhost:3000/",
        },
        redirect: "if_required",
      })
      .then((result) => {
        if (result) {
          // Inform the customer that there was an error.
          saveOrderToDB(result);
          dispatch(openPayment());
        }
      })
      .catch((error) => {
        if (error) {
          setMessage("An unexpected error occurred.");
        }
      });

    setIsLoading(false);
  };

  switch (step) {
    case 1:
      return (
        <form id="payment-form" onSubmit={submitFormData}>
          <div className="flex flex-col p-[40px] relative">
            <IoMdClose
              onClick={() => dispatch(openPayment())}
              className="hover:cursor-pointer absolute top-[13px] right-[13px] text-2xl text-gray-700"
            />
            <label
              htmlFor="name"
              className="mb-[0.25rem] text-[0.93rem] text-gray-600 font-normal"
            >
              Full name
            </label>
            <input
              className="outline-primary outline-[0.75rem] bg-white rounded-[5px] border border-[#e6e6e6] drop-shadow-sm p-[0.75rem] text-gray-500"
              id="name"
              style={{ border: error ? "2px solid red" : "" }}
              name="fullName"
              defaultValue={formData.fullName}
              type="text"
              placeholder="Full name"
              onChange={(e) => handleInputData(e)}
            />
            {error && <p style={{ color: "red" }}>This is a required field</p>}

            <label
              htmlFor="email"
              className="mb-[0.25rem] text-[0.93rem] text-gray-600 font-normal"
            >
              Email
            </label>
            <input
              className="outline-primary outline-[0.75rem] bg-white rounded-[5px] border border-[#e6e6e6] drop-shadow-sm p-[0.75rem] text-gray-500"
              id="email"
              style={{ border: error ? "2px solid red" : "" }}
              name="email"
              defaultValue={formData.email}
              type="email"
              placeholder="Email"
              onChange={(e) => handleInputData(e)}
            />
            {error && <p style={{ color: "red" }}>This is a required field</p>}

            <label
              htmlFor="country"
              className="mb-[0.25rem] text-[0.93rem] text-gray-600 font-normal"
            >
              Country
            </label>
            <input
              className="outline-primary outline-[0.75rem] bg-white rounded-[5px] border border-[#e6e6e6] drop-shadow-sm p-[0.75rem] text-gray-500"
              id="country"
              style={{ border: error ? "2px solid red" : "" }}
              name="country"
              defaultValue={formData.country}
              type="text"
              placeholder="Country"
              onChange={(e) => handleInputData(e)}
            />
            {error && <p style={{ color: "red" }}>This is a required field</p>}

            <label
              htmlFor="address"
              className="mb-[0.25rem] text-[0.93rem] text-gray-600 font-normal"
            >
              Address
            </label>
            <input
              className="outline-primary outline-[0.75rem] bg-white rounded-[5px] border border-[#e6e6e6] drop-shadow-sm p-[0.75rem] text-gray-500"
              id="address"
              style={{ border: error ? "2px solid red" : "" }}
              name="address"
              defaultValue={formData.address}
              type="text"
              placeholder="Address"
              onChange={(e) => handleInputData(e)}
            />
            {error && <p style={{ color: "red" }}>This is a required field</p>}

            <button
              onClick={() => setStep(step + 1)}
              type="submit"
              className="submit mt-2 py-3 px-8"
            >
              Continue
            </button>
          </div>
        </form>
      );
    case 2:
      return (
        <form id="payment-form" onSubmit={handleSubmit}>
          <div className="p-[40px] relative">
            <IoMdClose
              onClick={() => dispatch(openPayment())}
              className="hover:cursor-pointer absolute top-[13px] right-[13px] text-2xl text-gray-700"
            />
            <PaymentElement id="payment-element" />
            <div className="flex gap-2">
              <button
                className="w-fit py-2 px-5 border-2 bg-white border-gray-700 text-gray-700 text-base font-Roboto font-medium rounded-full hover:text-gray-800 hover:border-gray-800 transition drop-shadow-md"
                onClick={() => setStep(step - 1)}
              >
                Back
              </button>

              <button
                disabled={isLoading || !stripe || !elements}
                id="submit"
                className="submit w-fit py-2 px-8"
              >
                <span id="button-text">
                  {isLoading ? (
                    <div className="spinner" id="spinner"></div>
                  ) : (
                    "Pay now"
                  )}
                </span>
              </button>
            </div>
            {/* Show any error or success messages */}
            {message && <div id="payment-message">{message}</div>}
          </div>
        </form>
      );
    default:
      return (
        <div className="App">
          <p
            onClick={() => dispatch(openPayment())}
            className="text-gray-600 hover:cursor-pointer hover:underline font-Roboto font-normal w-fit text-center"
          >
            Close
          </p>
        </div>
      );
  }
}
